/**
 * Single source of truth for backend API URLs across environments.
 *
 * Used by:
 * - `vite.config.js`  → sets the `/api` dev proxy target for `npm run dev`
 * - `src/config/env.ts` → fallback `DEFAULT_API_BASE_URL` for runtime
 *
 * Per-branch usage:
 * - dev branch  → `ACTIVE_ENV = 'development'`
 * - main branch → `ACTIVE_ENV = 'production'`
 *
 * Can also be overridden at build time without editing this file:
 *   VITE_DEPLOY_ENV=production npm run build
 *
 * Note: the deployed SPA still auto-detects the API from its own hostname
 * via `isDeployedSpaHost()` in `src/config/env.ts`, so production builds
 * served at healthlynk.ai / dev.healthlynk.ai use same-origin `/api`
 * regardless of this value. ACTIVE_ENV only matters for:
 *   1. Local `npm run dev` proxy target.
 *   2. Runtime fallback when the SPA is served from an unrecognized host.
 */

export const API_ENVIRONMENTS = {
  development: {
    name: 'development',
    baseUrl: 'https://dev.healthlynk.ai',
    hosts: ['dev.healthlynk.ai', 'www.dev.healthlynk.ai'],
  },
  production: {
    name: 'production',
    baseUrl: 'https://healthlynk.ai',
    hosts: ['healthlynk.ai', 'www.healthlynk.ai'],
  },
}

/**
 * Active environment for this branch.
 * Change ONLY this line when promoting dev → main (or set VITE_DEPLOY_ENV
 * in the build env / CI pipeline to override without editing source).
 */
const FALLBACK_ACTIVE_ENV = 'development'

function readDeployEnvFromImportMeta() {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const value = import.meta.env.VITE_DEPLOY_ENV
      if (typeof value === 'string' && value.trim()) {
        return value.trim()
      }
    }
  } catch {
    /* import.meta not available (e.g. vite.config.js node context) */
  }
  return ''
}

function readDeployEnvFromProcess() {
  try {
    if (typeof process !== 'undefined' && process.env && process.env.VITE_DEPLOY_ENV) {
      return String(process.env.VITE_DEPLOY_ENV).trim()
    }
  } catch {
    /* process not available (browser) */
  }
  return ''
}

function resolveActiveEnv() {
  const fromImport = readDeployEnvFromImportMeta()
  if (fromImport && API_ENVIRONMENTS[fromImport]) return fromImport
  const fromProcess = readDeployEnvFromProcess()
  if (fromProcess && API_ENVIRONMENTS[fromProcess]) return fromProcess
  return FALLBACK_ACTIVE_ENV
}

export const ACTIVE_ENV = resolveActiveEnv()

/** Convenience accessors. */
export function getActiveApiBaseUrl() {
  return API_ENVIRONMENTS[ACTIVE_ENV].baseUrl
}

export function getActiveApiHosts() {
  return API_ENVIRONMENTS[ACTIVE_ENV].hosts
}
